import {
  ALLOWED_FILTER_TOKENS,
  DATE_PARAM_KEY,
  FILE_TYPE_PARAM_KEY,
  type FilterKeys,
  HIGHLIGHT_END_PARAM_KEY,
  HIGHLIGHT_START_PARAM_KEY,
  INCLUDE_PARAM_KEY,
  MODE_PARAM_KEY,
  OBJECT_TYPE_PARAM_KEY,
  PROJECTS_PARAM_KEY,
  QUERY_PARAM_KEY,
  SORT_PARAM_KEY,
  TAB_PARAM_KEY,
  WORKSPACES_PARAM_KEY,
} from '../../search/constants';
import { createUrl } from '../helpers/createUrl';
import { getPathnameFromHref } from '../helpers/getPathnameFromHref';
import type { ValidHref } from '../types';

export type SearchGetLinkParams = {
  page: 'search';
  query?: string | null;
  mode?: 'standard' | 'ai';
  // @todo (SRCH-2455): Remove outgoing legacy tab support after M2 rollout.
  tab?: 'standard' | 'ai';
  highlightStart?: number | null;
  highlightEnd?: number | null;
  // Legacy Filters
  filters?: Partial<Record<FilterKeys, boolean | null>> | null;
  projectId?: string | null;
  // Advanced Filters
  date?: string;
  file_type?: string;
  include?: string;
  object_type?: string;
  projects?: string;
  sort?: string;
  workspaces?: string;
};

const ADVANCED_PARAM_KEYS: (keyof SearchGetLinkParams)[] = [
  OBJECT_TYPE_PARAM_KEY,
  FILE_TYPE_PARAM_KEY,
  INCLUDE_PARAM_KEY,
  PROJECTS_PARAM_KEY,
  DATE_PARAM_KEY,
  SORT_PARAM_KEY,
  WORKSPACES_PARAM_KEY,
];

const isAllowedFilterKey = (key: string): key is FilterKeys => {
  return ALLOWED_FILTER_TOKENS.has(key as FilterKeys);
};

export const getLinkForSearch = (params: SearchGetLinkParams): ValidHref => {
  let newUrl = createUrl({ pathname: '/search' });
  const urlSearchParams = new URLSearchParams();

  // ... ?query=:query
  if ('query' in params && params.query) {
    urlSearchParams.set(QUERY_PARAM_KEY, params.query);
  }

  // Legacy => Advanced search filters
  // convert any legacy filters to the corresponding advanced search filters
  // legacy filters still generated from quicksearch by useNavigateToAdvancedSearch
  // intentionally overwritten if OBJECT_TYPE_PARAM_KEY is set later
  // ... ?object_type=:a,p,f
  if ('filters' in params && params.filters) {
    const selectedFilters = Object.entries(params.filters).reduce<FilterKeys[]>(
      (acc, [key, value]) => {
        const isAllowedKey = isAllowedFilterKey(key);
        if (value && isAllowedKey) {
          acc.push(key);
        }
        return acc;
      },
      []
    );

    if (selectedFilters.length > 0) {
      urlSearchParams.set(OBJECT_TYPE_PARAM_KEY, selectedFilters.join(','));
    }
  }

  // intentionally overwritten if PROJECTS_PARAM_KEY is set later
  // ... ?projects=:projectId
  if ('projectId' in params && params.projectId) {
    urlSearchParams.set(PROJECTS_PARAM_KEY, params.projectId);
  }

  // Advanced search filters (preferred over legacy)
  ADVANCED_PARAM_KEYS.forEach((paramKey) => {
    if (paramKey in params && params[paramKey]) {
      urlSearchParams.set(paramKey, params[paramKey] as string);
    }
  });

  // @todo (SRCH-2455): Remove outgoing tab serialization after M2 rollout.
  // Legacy & Advanced search filters
  // ... ?tab=:tab
  if ('tab' in params && params.tab) {
    urlSearchParams.set(TAB_PARAM_KEY, params.tab);
  }

  if ('mode' in params && params.mode) {
    urlSearchParams.set(MODE_PARAM_KEY, params.mode);
  }

  // ... ?highlightStart=:highlightStart&highlightEnd=:highlightEnd
  if (
    'highlightStart' in params &&
    'highlightEnd' in params &&
    params.highlightStart != null &&
    params.highlightEnd != null
  ) {
    urlSearchParams.set(
      HIGHLIGHT_START_PARAM_KEY,
      String(params.highlightStart)
    );
    urlSearchParams.set(HIGHLIGHT_END_PARAM_KEY, String(params.highlightEnd));
  }

  if (urlSearchParams.toString()) {
    newUrl += `?${urlSearchParams.toString()}`;
  }

  return newUrl as ValidHref;
};

export const isSearchPageType = (href: ValidHref): boolean => {
  const pathname = getPathnameFromHref(href);
  return pathname === '/search';
};
